import React, { useCallback, useEffect, useRef, useState } from "react";
import { Dimensions, StyleSheet, View } from "react-native";
import { useFocusEffect, useNavigation, useRoute } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import type { RootStackParamList } from "../../../navigation/navigation.types";
import Loader from "../../../components/common/status/Loader/Loader";
import BlankLayout from "../../../layouts/BlankLayout";
import ErrorLoader from "../../../components/common/status/ErrorLoader/ErrorLoader";
import AssessmentBeginModal from "../sharedFiles/AssessmentBeginModal";
import {
  type SubmitOralAssessmentAnswerInput,
  type StartOralAssessmentSessionMutation,
  useGetAssessmentQuery,
  useGetCourseCoreQuery,
  useGetRunningAssessmentSubmissionByUserQuery,
  useGetAssessmentSubmissionAttemptHistoryQuery,
  useStartOralAssessmentSessionMutation,
  useSubmitOralAssessmentAnswerMutation,
  useEndOralAssessmentSessionMutation,
  useGetOralAssessmentSessionStateLazyQuery,
} from "@axio/graphql/generated/graphql";
import AssessmentCompleteModal from "../sharedFiles/AssessmentCompleteModal";
import ExitBeforeCompletedModal from "../sharedFiles/ExitBeforeCompletedModal";
import CursorLeavesWindowModal from "../sharedFiles/CursorLeavesWindowModal";
import AssessmentInProgressModal from "../sharedFiles/AssessmentInProgressModal";
import AssessmentErrorModal from "../sharedFiles/AssessmentErrorModal";
import AssessmentBlockedModal from "../sharedFiles/AssessmentBlockedModal";
import {
  startSseAssessment,
  endSseAssessment,
  continueSseAssessment,
  getSseAssessmentTimer,
} from "../sharedFiles/AssessmentUtils";
import * as Sentry from "@sentry/react-native";
import { Audio } from "expo-av";
import { Platform } from "react-native";
import QuestionCounter from "../sharedFiles/AssessmentQuestionCounter";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import {
  QUESTION_COUNTER_HEIGHT,
  SIMPLE_VOICE_ASSISTANT_HEIGHT_TURN_BASED,
  ASSESSMENT_MENU_HEIGHT,
  ASSESSMENT_MENU_HEIGHT_MOBILE,
  BOTTOM_SPACING_HEIGHT,
  CONTENT_CONTAINER_PADDING,
} from "../../../constants/assessmentConstants";
import AssessmentSpeakingOrb from "../sharedFiles/AssessmentSpeakingOrb";
import AssessmentMenu from "../../../components/navigation/AssessmentMenu/AssessmentMenu";
import { formatCountdownFromSeconds } from "../../../utils/timeUtils";
import { useTimerAnnouncements } from "../../../hooks/useTimerAnnouncements";
import TurnBasedAssessmentAudioRenderer from "./TurnBasedAssessmentAudioRenderer";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import { ActivityIndicator } from "react-native-paper";
import useTurnBasedAssessmentSSE from "../../../hooks/useTurnBasedAssessmentSSE";
import { Microphone } from "@phosphor-icons/react/dist/csr/Microphone";
import { usePreviewMode } from "../../../contexts/PreviewModeContext";
import { useAuth } from "../../../hooks/useAuth";
import { useAppConfig } from "../../../AppConfigProvider";
import { getValidAccessToken, refreshAccessToken } from "../../../utils/authRefresh";

interface TurnBasedAssessmentProps {
  assessmentId: string;
  courseId: string;
  integrationId: string;
  moduleId?: string;
}

type OralSessionState = NonNullable<
  NonNullable<StartOralAssessmentSessionMutation["startOralAssessmentSession"]>["data"]
>;

const TurnBasedAssessment = ({ assessmentId, courseId, integrationId, moduleId }: TurnBasedAssessmentProps) => {
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute();
  const stayOnCompletion = route.name === "LTIAssessmentTaking";
  const shouldSkipCourseCoreQuery = route.name === "LTIAssessmentTaking" && !courseId;
  const { userInfo } = useAuth();
  const appConfig = useAppConfig();
  const oralAssessmentV2Enabled = Boolean(appConfig?.oralAssessmentV2Enabled);

  const [beginModalVisible, setBeginModalVisible] = useState(false);
  const [completeModalVisible, setCompleteModalVisible] = useState(false);
  const [exitModalVisible, setExitModalVisible] = useState(false);
  const [anotherAssessmentInProgressModalVisible, setAnotherAssessmentInProgressModalVisible] = useState(false);
  const [cursorLeavesWindowModalVisible, setCursorLeavesWindowModalVisible] = useState(false);
  const [orbLoading, setOrbLoading] = useState(true);
  const [loadingAssessmentSubmission, setLoadingAssessmentSubmission] = useState(false);
  const [assessmentErrorModalVisible, setAssessmentErrorModalVisible] = useState(false);
  const [assessmentErrorMessage, setAssessmentErrorMessage] = useState(
    "An error occurred while running the assessment. Please try again."
  );
  const [selectedMicrophoneId, setSelectedMicrophoneId] = useState("");
  const [questionIndex, setQuestionIndex] = useState(0);
  const [assessmentBlockedModalVisible, setAssessmentBlockedModalVisible] = useState(false);
  const [assessmentTimeLimit, setAssessmentTimeLimit] = useState<number | null>(null);
  const hasTimeLimit = (assessmentTimeLimit ?? 0) > 0;
  const [assessmentNumberOfQuestions, setAssessmentNumberOfQuestions] = useState<number | null>(null);
  const [oralSessionSubmissionId, setOralSessionSubmissionId] = useState<string | null>(null);
  const [isIntroPlaying, setIsIntroPlaying] = useState(false);
  const [audioLevel, setAudioLevel] = useState(0);
  const [introText, setIntroText] = useState<string | null>(null);
  const [introAudio, setIntroAudio] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recording, setRecording] = useState<Audio.Recording | null>(null);
  const [canAnswer, setCanAnswer] = useState(false);
  const [isProcessingAnswer, setIsProcessingAnswer] = useState(false);
  const [isAssessmentCompleting, setIsAssessmentCompleting] = useState(false);
  const [currentQuestionText, setCurrentQuestionText] = useState<string | null>(null);
  const [currentQuestionAudio, setCurrentQuestionAudio] = useState<string | null>(null);
  const [questionAudioMissing, setQuestionAudioMissing] = useState(false);
  const [isIntroComplete, setIsIntroComplete] = useState(false);
  const isIntroCompleteRef = useRef(false);
  const resetSequenceRef = useRef<(() => void) | null>(null);
  const audioQueueRef = useRef<{ seq: number; text: string; audio: string }[]>([]);
  const audioQueueCallbackRef = useRef<((chunk: { seq: number; text: string; audio: string }) => void) | null>(null);
  const processRemainingChunksRef = useRef<(() => void) | null>(null);
  const [displayTime, setDisplayTime] = useState<string | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const lastChunkAtRef = useRef<number | null>(null);
  const sseWatchdogRef = useRef<NodeJS.Timeout | null>(null);
  const endTimeRef = useRef<number | null>(null);
  const assessmentIdRef = useRef<string | null>(null);
  const retryCountRef = useRef<number>(0);
  const timerSyncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isSyncingTimerRef = useRef<boolean>(false);
  const startAssessmentInFlightRef = useRef(false);
  const continueAssessmentInFlightRef = useRef(false);
  const runningSessionInitializedRef = useRef(false);
  const {
    announcement: timerAnnouncement,
    isAssertive: timerAnnouncementAssertive,
    checkAnnouncement,
    resetAnnouncements,
  } = useTimerAnnouncements();

  const [recordingAnnouncement, setRecordingAnnouncement] = useState<string>("");
  const stopAudioRef = useRef<(() => void) | null>(null);

  const captureAssessmentError = useCallback((message: string, error?: unknown) => {
    if (error instanceof Error) {
      Sentry.captureException(error);
      return;
    }

    Sentry.captureException(new Error(message));
  }, []);
  const handleRendererIntroStart = useCallback(() => {
    setIsIntroPlaying(true);
  }, []);
  const handleRendererIntroComplete = useCallback(() => {
    setIsIntroPlaying(false);
  }, []);
  const { isMedium } = useResponsiveScreen();
  const { isPreviewMode } = usePreviewMode();
  const { height } = Dimensions.get("window");
  const PREVIEW_BANNER_HEIGHT = 50;
  const dynamicHeight = Math.max(
    5,
    height -
      QUESTION_COUNTER_HEIGHT -
      SIMPLE_VOICE_ASSISTANT_HEIGHT_TURN_BASED -
      (isMedium ? ASSESSMENT_MENU_HEIGHT : ASSESSMENT_MENU_HEIGHT_MOBILE) -
      BOTTOM_SPACING_HEIGHT -
      CONTENT_CONTAINER_PADDING * 2 -
      (isPreviewMode ? PREVIEW_BANNER_HEIGHT : 0)
  );

  const handleMicrophoneSelect = (deviceId: string) => {
    setSelectedMicrophoneId(deviceId);
  };

  const { refetch: refetchCourseData } = useGetCourseCoreQuery({
    variables: {
      id: courseId,
    },
    skip: shouldSkipCourseCoreQuery,
  });

  const {
    data: assessment,
    loading: assessmentLoading,
    error: assessmentError,
    refetch: refetchAssessment,
  } = useGetAssessmentQuery({
    variables: {
      assessmentId: assessmentId,
    },
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: assessmentSubmission,
    loading: assessmentSubmissionLoading,
    error: assessmentSubmissionError,
    refetch: refetchAssessmentSubmission,
  } = useGetRunningAssessmentSubmissionByUserQuery({
    skip: !assessmentId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: assessmentAttemptHistory,
    loading: assessmentAttemptHistoryLoading,
    refetch: refetchAssessmentAttemptHistory,
  } = useGetAssessmentSubmissionAttemptHistoryQuery({
    variables: {
      assessmentId,
      userId: userInfo.userId,
    },
    skip: !assessmentId || !userInfo.userId,
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const [startOralAssessmentSessionMutation] = useStartOralAssessmentSessionMutation();
  const [submitOralAssessmentAnswerMutation] = useSubmitOralAssessmentAnswerMutation();
  const [endOralAssessmentSessionMutation] = useEndOralAssessmentSessionMutation();
  const [getOralAssessmentSessionStateLazyQuery] = useGetOralAssessmentSessionStateLazyQuery({
    fetchPolicy: "network-only",
  });

  const assessmentSubmissionData = assessmentSubmission?.getRunningAssessmentSubmissionByUser?.data;

  const assessmentSubmissionIsRunningInDatabase = assessmentSubmissionData?.status === "RUNNING";
  const runningAssessmentId = assessmentSubmissionIsRunningInDatabase
    ? (assessmentSubmissionData?.assessment?.id ?? null)
    : null;

  const userAlreadyHasAnotherRunningAssessment = Boolean(runningAssessmentId && runningAssessmentId !== assessmentId);

  const latestAssessmentAttempt = assessmentAttemptHistory?.getAttemptHistory?.data?.assessmentSubmissions?.[0];
  const latestRetakeDeadline = latestAssessmentAttempt?.retakeDeadline
    ? new Date(latestAssessmentAttempt.retakeDeadline)
    : null;
  const latestRetakeDeadlinePassed = latestRetakeDeadline ? latestRetakeDeadline < new Date() : false;
  const assessmentSubmissionIsCompletedAndRetakeDenied =
    latestAssessmentAttempt?.status === "COMPLETED" &&
    (latestAssessmentAttempt?.retakeAllowed !== true || latestRetakeDeadlinePassed);

  const assessmentIsRunning =
    (runningAssessmentId === assessmentId && assessmentSubmissionIsRunningInDatabase) ||
    (oralAssessmentV2Enabled && !!oralSessionSubmissionId);

  const cleanupCountdown = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (timerSyncIntervalRef.current) {
      clearInterval(timerSyncIntervalRef.current);
      timerSyncIntervalRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    if (sseWatchdogRef.current) {
      clearInterval(sseWatchdogRef.current);
      sseWatchdogRef.current = null;
    }
    endTimeRef.current = null;
    remainingSecondsRef.current = 0;
    timerStartTimeRef.current = null;
    retryCountRef.current = 0;
    assessmentIdRef.current = null;
    isSyncingTimerRef.current = false;
  }, []);

  const handleAssessmentComplete = useCallback(() => {
    cleanupCountdown();
    setOralSessionSubmissionId(null);
    setLoadingAssessmentSubmission(false);
    setCompleteModalVisible(true);
    setDisplayTime("00:00");
  }, [cleanupCountdown]);

  const remainingSecondsRef = useRef<number>(0);
  const timerStartTimeRef = useRef<number | null>(null);

  const updateRemaining = useCallback(
    (remainingSec: number) => {
      setDisplayTime(formatCountdownFromSeconds(remainingSec));
      if (remainingSec <= 0 && intervalRef.current) {
        clearInterval(intervalRef.current);
        if (oralAssessmentV2Enabled) {
          if (oralSessionSubmissionId) {
            endOralAssessmentSessionMutation({
              variables: {
                submissionId: oralSessionSubmissionId,
              },
            })
              .catch(() => {
                captureAssessmentError("Error ending oral session on timeout");
              })
              .finally(() => {
                handleAssessmentComplete();
              });
          } else {
            captureAssessmentError("Oral session submission id missing on timeout");
            handleAssessmentComplete();
          }
          return;
        }

        endSseAssessment(() => {
          captureAssessmentError("Error ending assessment on timeout");
        }).then(() => {
          handleAssessmentComplete();
        });
      }
    },
    [endOralAssessmentSessionMutation, handleAssessmentComplete, oralAssessmentV2Enabled, oralSessionSubmissionId]
  );

  const syncTimerWithServer = useCallback(async () => {
    if (isSyncingTimerRef.current) {
      return;
    }
    isSyncingTimerRef.current = true;
    try {
      if (oralAssessmentV2Enabled && oralSessionSubmissionId) {
        const response = await getOralAssessmentSessionStateLazyQuery({
          variables: {
            submissionId: oralSessionSubmissionId,
          },
        });
        const session = response.data?.getOralAssessmentSessionState?.data;
        if (session?.remainingSec !== undefined) {
          const remainingSec = Math.max(0, Math.floor(session.remainingSec));
          remainingSecondsRef.current = remainingSec;
          timerStartTimeRef.current = Date.now();
          updateRemaining(remainingSec);
        }
        return;
      }

      const timerData = await getSseAssessmentTimer();
      if (timerData && timerData.remainingSec !== undefined) {
        remainingSecondsRef.current = timerData.remainingSec;
        timerStartTimeRef.current = Date.now();
        updateRemaining(timerData.remainingSec);
      }
    } catch (error) {
      captureAssessmentError("Error syncing timer with server", error);
    } finally {
      isSyncingTimerRef.current = false;
    }
  }, [
    captureAssessmentError,
    getOralAssessmentSessionStateLazyQuery,
    oralAssessmentV2Enabled,
    oralSessionSubmissionId,
    updateRemaining,
  ]);

  const startTimer = useCallback(
    (remainingSec: number) => {
      resetAnnouncements();
      remainingSecondsRef.current = remainingSec;
      timerStartTimeRef.current = Date.now();
      updateRemaining(remainingSec);
      checkAnnouncement(remainingSec);

      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      intervalRef.current = setInterval(() => {
        if (remainingSecondsRef.current > 0) {
          remainingSecondsRef.current = Math.max(0, remainingSecondsRef.current - 1);
          updateRemaining(remainingSecondsRef.current);
          checkAnnouncement(remainingSecondsRef.current);
        }
      }, 1000);

      if (timerSyncIntervalRef.current) {
        clearInterval(timerSyncIntervalRef.current);
      }
      timerSyncIntervalRef.current = setInterval(() => {
        syncTimerWithServer();
      }, 5_000);
    },
    [updateRemaining, syncTimerWithServer, checkAnnouncement, resetAnnouncements]
  );

  const handleVisibilityChange = useCallback(() => {
    if (!assessmentIsRunning) {
      return;
    }
    if (document.hidden) {
      setCursorLeavesWindowModalVisible(true);
    }
  }, [assessmentIsRunning]);

  const handleMouseLeave = useCallback(
    (event) => {
      if (!assessmentIsRunning) {
        return;
      }
      const { clientX, clientY } = event;

      const leftEdge = clientX <= 0;
      const rightEdge = clientX >= window.innerWidth;
      const topEdge = clientY <= 0;
      const bottomEdge = clientY >= window.innerHeight;

      if (leftEdge || rightEdge || topEdge || bottomEdge) {
        setCursorLeavesWindowModalVisible(true);
      }
    },
    [assessmentIsRunning]
  );

  const openSSE = useTurnBasedAssessmentSSE({
    eventSourceRef,
    isIntroCompleteRef,
    resetSequenceRef,
    audioQueueRef,
    audioQueueCallbackRef,
    processRemainingChunksRef,
    lastChunkAtRef,
    sseWatchdogRef,
    endTimeRef,
    intervalRef,
    assessmentIdRef,
    retryCountRef,
    setAssessmentErrorModalVisible,
    setQuestionIndex,
    setIsIntroComplete,
    setIntroText,
    setIntroAudio,
    setCurrentQuestionText,
    setCurrentQuestionAudio,
    setCanAnswer,
    setIsRecording,
    setIsProcessingAnswer,
    setDisplayTime,
    setRecording,
    recording,
    updateRemaining,
    handleAssessmentComplete,
    cleanupCountdown,
    handleVisibilityChange,
    handleMouseLeave,
  });

  const resetTurnBasedAssessmentState = useCallback(async () => {
    setIntroText(null);
    setIntroAudio(null);
    setOralSessionSubmissionId(null);
    setCurrentQuestionText(null);
    setCurrentQuestionAudio(null);
    setQuestionAudioMissing(false);
    setIsIntroComplete(true);
    isIntroCompleteRef.current = true;
    setCanAnswer(false);
    setQuestionIndex(0);
    setIsRecording(false);
    setIsProcessingAnswer(false);
    setIsAssessmentCompleting(false);
    if (recording) {
      await recording.stopAndUnloadAsync().catch((error) => {
        captureAssessmentError("Error resetting recording state", error);
      });
      setRecording(null);
    }
    if (resetSequenceRef.current) {
      resetSequenceRef.current();
    }
    audioQueueRef.current = [];
    audioQueueCallbackRef.current = null;
  }, [captureAssessmentError, recording]);

  const openAssessmentStreamFromStartResponse = useCallback(
    (
      response: {
        startedAt: string | number;
        durationSec: number;
        remainingSec?: number | null;
      },
      id: string
    ) => {
      const remainingSec = response.remainingSec ?? response.durationSec;
      if (hasTimeLimit && remainingSec > 0) {
        startTimer(remainingSec);
      } else {
        cleanupCountdown();
        setDisplayTime(null);
      }

      const startTime =
        typeof response.startedAt === "number" ? response.startedAt : new Date(response.startedAt).getTime();
      const endTime = startTime + response.durationSec * 1000;
      openSSE(id, endTime);
    },
    [cleanupCountdown, hasTimeLimit, openSSE, startTimer]
  );

  const transcribeAudioInBackground = useCallback(
    async (audioSource: Blob | { uri: string; name: string; type: string }) => {
      const token = await getValidAccessToken({ minTtlSeconds: 30 });
      if (!token) {
        throw new Error("missing_access_token");
      }

      const buildFormData = () => {
        const formData = new FormData();
        if (Platform.OS === "web") {
          const webAudioSource = audioSource as Blob & { name?: string };
          formData.append("audio", webAudioSource, webAudioSource.name || "recording.webm");
        } else {
          formData.append("audio", audioSource as unknown as Blob);
        }
        return formData;
      };

      let response = await fetch("/api/audio/transcribe", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: buildFormData(),
      });

      if (response.status === 401 || response.status === 403) {
        const refreshed = await refreshAccessToken();
        if (refreshed?.accessToken) {
          response = await fetch("/api/audio/transcribe", {
            method: "POST",
            headers: {
              Authorization: `Bearer ${refreshed.accessToken}`,
            },
            body: buildFormData(),
          });
        }
      }

      if (!response.ok) {
        throw new Error(`transcription_failed_${response.status}`);
      }

      const data = await response.json();
      return (data?.text || "").trim();
    },
    []
  );

  const applyOralSessionState = useCallback(
    (session: OralSessionState) => {
      setOralSessionSubmissionId(session.submissionId);
      const currentQuestionIndex = session.currentTurn?.questionIndex ?? 0;
      const fallbackCurrentTurn =
        session.questions.find((question) => question.questionIndex === currentQuestionIndex) ??
        session.questions[currentQuestionIndex] ??
        null;
      const resolvedCurrentTurn = session.currentTurn ?? fallbackCurrentTurn;
      setIntroText(session.introText ?? null);
      setIntroAudio(session.introAudioUrl ?? null);
      setCurrentQuestionText(resolvedCurrentTurn?.questionText ?? null);
      setCurrentQuestionAudio(resolvedCurrentTurn?.questionAudioUrl ?? null);
      setQuestionAudioMissing(Boolean(resolvedCurrentTurn?.questionText) && !resolvedCurrentTurn?.questionAudioUrl);
      setCanAnswer(session.status === "AWAITING_ANSWER");
      setIsProcessingAnswer(false);
      setQuestionIndex(currentQuestionIndex + 1);
      setIsIntroComplete(true);
      isIntroCompleteRef.current = true;
      const remainingSec = Math.max(0, Math.floor(session.remainingSec));
      if (hasTimeLimit && remainingSec > 0) {
        startTimer(remainingSec);
      } else {
        cleanupCountdown();
        setDisplayTime(null);
      }

      if (session.status === "COMPLETED" || session.status === "TIMED_OUT") {
        handleAssessmentComplete();
      }
    },
    [cleanupCountdown, handleAssessmentComplete, hasTimeLimit, startTimer]
  );

  const syncOralSessionState = useCallback(
    async (submissionId: string) => {
      const response = await getOralAssessmentSessionStateLazyQuery({
        variables: {
          submissionId,
        },
      });

      const session = response.data?.getOralAssessmentSessionState?.data;
      if (!session) {
        return null;
      }

      applyOralSessionState(session);

      return session;
    },
    [applyOralSessionState, getOralAssessmentSessionStateLazyQuery]
  );

  const submitOralAnswerFromTranscript = useCallback(
    async ({
      submissionId,
      questionIndex,
      answerText,
      recordingEndedAtMs,
      transcriptionFailed,
      fallbackErrorLabel,
    }: {
      submissionId: string;
      questionIndex: number;
      answerText: string | null;
      recordingEndedAtMs: number;
      transcriptionFailed: boolean;
      fallbackErrorLabel: string;
    }) => {
      const input: SubmitOralAssessmentAnswerInput = {
        submissionId,
        questionIndex,
        answerText,
        recordingEndedAtMs,
        transcriptionFailed,
      };

      try {
        await submitOralAssessmentAnswerMutation({
          variables: { input },
        });
      } catch (submitError) {
        captureAssessmentError(fallbackErrorLabel, submitError);
      }

      try {
        const syncedSession = await syncOralSessionState(submissionId);
        if (!syncedSession) {
          setCanAnswer(true);
        }
      } catch (syncError) {
        captureAssessmentError(`${fallbackErrorLabel}: sync failed`, syncError);
        setCanAnswer(true);
      }
    },
    [captureAssessmentError, submitOralAssessmentAnswerMutation, syncOralSessionState]
  );

  const submitV2AnswerFromAudioSource = useCallback(
    ({
      audioSource,
      recordingEndedAtMs,
      questionAudioTranscriptionLabel,
    }: {
      audioSource: Blob | { uri: string; name: string; type: string };
      recordingEndedAtMs: number;
      questionAudioTranscriptionLabel: string;
    }) => {
      const currentSubmissionId = oralSessionSubmissionId;
      const currentQuestionIndex = Math.max(0, questionIndex - 1);

      void (async () => {
        try {
          const transcript = await transcribeAudioInBackground(audioSource);
          if (!currentSubmissionId) {
            return;
          }
          await submitOralAnswerFromTranscript({
            submissionId: currentSubmissionId,
            questionIndex: currentQuestionIndex,
            answerText: transcript || null,
            recordingEndedAtMs,
            transcriptionFailed: false,
            fallbackErrorLabel: `${questionAudioTranscriptionLabel}: submit failed`,
          });
        } catch (error) {
          captureAssessmentError(questionAudioTranscriptionLabel, error);
          if (currentSubmissionId) {
            await submitOralAnswerFromTranscript({
              submissionId: currentSubmissionId,
              questionIndex: currentQuestionIndex,
              answerText: null,
              recordingEndedAtMs,
              transcriptionFailed: true,
              fallbackErrorLabel: `${questionAudioTranscriptionLabel}: fallback submit failed`,
            });
          }
        } finally {
          setIsProcessingAnswer(false);
        }
      })();
    },
    [
      captureAssessmentError,
      oralSessionSubmissionId,
      questionIndex,
      submitOralAnswerFromTranscript,
      transcribeAudioInBackground,
    ]
  );

  const handleBeginAssessment = async () => {
    if (assessmentIsRunning || startAssessmentInFlightRef.current) {
      return;
    }
    startAssessmentInFlightRef.current = true;
    try {
      setLoadingAssessmentSubmission(true);
      await resetTurnBasedAssessmentState();

      if (oralAssessmentV2Enabled) {
        const response = await startOralAssessmentSessionMutation({
          variables: {
            assessmentId,
          },
        });

        const startResponse = response.data?.startOralAssessmentSession;
        const session = startResponse?.data;

        if (startResponse) {
          Sentry.addBreadcrumb({
            category: "oral-assessment-v2",
            message: "startOralAssessmentSession",
            level: "info",
            data: {
              assessmentId,
              success: startResponse.success,
              message: startResponse.message || null,
              hasData: Boolean(session),
              submissionId: session?.submissionId || null,
              status: session?.status || null,
            },
          });
        }

        if (startResponse?.success === false) {
          throw new Error(startResponse.message || "Failed to start oral session");
        }

        if (session) {
          runningSessionInitializedRef.current = true;
          applyOralSessionState(session);
          setBeginModalVisible(false);
          return;
        }

        throw new Error(response.data?.startOralAssessmentSession?.message || "Failed to start oral session");
      }

      const response = await startSseAssessment(assessmentId, handleSseAssessmentError);
      await refetchAssessmentSubmission();
      if (response?.startedAt != null && response?.durationSec != null) {
        openAssessmentStreamFromStartResponse(response, assessmentId);
        runningSessionInitializedRef.current = true;
      }
      setBeginModalVisible(false);
    } catch (error) {
      captureAssessmentError("Error starting assessment session", error);
      runningSessionInitializedRef.current = false;
      setBeginModalVisible(false);
      if (error instanceof Error && error.message) {
        setAssessmentErrorMessage(error.message);
      } else {
        setAssessmentErrorMessage("An error occurred while running the assessment. Please try again.");
      }
      setAssessmentErrorModalVisible(true);
    } finally {
      setLoadingAssessmentSubmission(false);
      startAssessmentInFlightRef.current = false;
    }
  };

  useEffect(() => {
    startAssessmentInFlightRef.current = false;
    continueAssessmentInFlightRef.current = false;
    runningSessionInitializedRef.current = false;
    setOralSessionSubmissionId(null);
    setBeginModalVisible(false);
    setCompleteModalVisible(false);
    setExitModalVisible(false);
    setAnotherAssessmentInProgressModalVisible(false);
    setCursorLeavesWindowModalVisible(false);
    setAssessmentErrorModalVisible(false);
    setAssessmentBlockedModalVisible(false);
    setIsAssessmentCompleting(false);
    setIsProcessingAnswer(false);
    setIsRecording(false);
    setCanAnswer(false);
    setQuestionIndex(0);
    setIntroText(null);
    setIntroAudio(null);
    setCurrentQuestionText(null);
    setCurrentQuestionAudio(null);
    cleanupCountdown();
  }, [assessmentId, cleanupCountdown]);

  useFocusEffect(
    useCallback(() => {
      const initializePageState = async () => {
        if (assessmentSubmissionLoading || assessmentAttemptHistoryLoading) {
          return;
        }

        if (startAssessmentInFlightRef.current || runningSessionInitializedRef.current) {
          return;
        }

        if (oralAssessmentV2Enabled && oralSessionSubmissionId) {
          return;
        }

        if (assessmentSubmissionIsCompletedAndRetakeDenied) {
          setAssessmentBlockedModalVisible(true);
          return;
        }

        if (userAlreadyHasAnotherRunningAssessment) {
          setAnotherAssessmentInProgressModalVisible(true);
          return;
        }

        if (assessmentSubmissionIsRunningInDatabase) {
          if (runningSessionInitializedRef.current || startAssessmentInFlightRef.current) {
            return;
          }
          startAssessmentInFlightRef.current = true;
          setLoadingAssessmentSubmission(true);
          try {
            await resetTurnBasedAssessmentState();

            if (oralAssessmentV2Enabled) {
              const currentSubmissionId = oralSessionSubmissionId;
              if (currentSubmissionId) {
                const session = await syncOralSessionState(currentSubmissionId);
                if (session) {
                  runningSessionInitializedRef.current = true;
                  return;
                }
              }

              const response = await startOralAssessmentSessionMutation({
                variables: {
                  assessmentId,
                },
              });
              const startResponse = response.data?.startOralAssessmentSession;
              const session = startResponse?.data;

              if (startResponse?.success === false) {
                throw new Error(startResponse.message || "Failed to restore oral session");
              }

              if (session) {
                runningSessionInitializedRef.current = true;
                applyOralSessionState(session);
                return;
              }

              throw new Error(response.data?.startOralAssessmentSession?.message || "Failed to restore oral session");
            }

            const timerData = await getSseAssessmentTimer();
            if (timerData) {
              const remainingSec = timerData.remainingSec ?? timerData.durationSec;
              if (hasTimeLimit && remainingSec > 0) {
                startTimer(remainingSec);
              } else {
                cleanupCountdown();
                setDisplayTime(null);
              }
              if (timerData.questionIndex !== undefined) {
                setQuestionIndex(timerData.questionIndex + 1);
              }
              if (timerData.introPlayed !== undefined) {
                setIsIntroComplete(timerData.introPlayed);
                isIntroCompleteRef.current = timerData.introPlayed;
              }
              const endTime = timerData.startTime + timerData.durationSec * 1000;
              openSSE(assessmentId, endTime);
              runningSessionInitializedRef.current = true;
              return;
            }

            const response = await startSseAssessment(assessmentId, handleSseAssessmentError);
            if (response?.startedAt != null && response?.durationSec != null) {
              openAssessmentStreamFromStartResponse(response, assessmentId);
              runningSessionInitializedRef.current = true;
            }
          } catch (error) {
            captureAssessmentError("Error restoring assessment session", error);
            runningSessionInitializedRef.current = false;
            if (error instanceof Error && error.message) {
              setAssessmentErrorMessage(error.message);
            } else {
              setAssessmentErrorMessage("An error occurred while running the assessment. Please try again.");
            }
            setAssessmentErrorModalVisible(true);
          } finally {
            setLoadingAssessmentSubmission(false);
            startAssessmentInFlightRef.current = false;
          }
          return;
        }

        const showBeginModal = !userAlreadyHasAnotherRunningAssessment && !oralSessionSubmissionId;

        if (showBeginModal) {
          runningSessionInitializedRef.current = false;
          setBeginModalVisible(true);
          return;
        }
      };

      if (!assessmentSubmissionLoading) {
        initializePageState();
      }
    }, [
      assessmentId,
      assessmentSubmissionLoading,
      assessmentSubmission,
      assessmentAttemptHistoryLoading,
      assessmentAttemptHistory,
      captureAssessmentError,
      resetTurnBasedAssessmentState,
      startTimer,
      openSSE,
      openAssessmentStreamFromStartResponse,
      oralAssessmentV2Enabled,
      applyOralSessionState,
      oralSessionSubmissionId,
      syncOralSessionState,
      hasTimeLimit,
      startOralAssessmentSessionMutation,
    ])
  );

  const handleSseAssessmentError = () => {
    setLoadingAssessmentSubmission(false);
    setBeginModalVisible(false);
    setAssessmentErrorModalVisible(true);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
    document.removeEventListener("mouseleave", handleMouseLeave);
  };

  const stopAssessmentAudio = useCallback(() => {
    if (stopAudioRef.current) {
      stopAudioRef.current();
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      document.addEventListener("visibilitychange", handleVisibilityChange);
      document.addEventListener("mouseleave", handleMouseLeave);
      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange);
        document.removeEventListener("mouseleave", handleMouseLeave);
      };
    }, [assessmentIsRunning])
  );

  useFocusEffect(
    useCallback(() => {
      if (!assessmentId) {
        return;
      }
      refetchAssessment();
      refetchAssessmentSubmission();
      refetchAssessmentAttemptHistory();
    }, [assessmentId])
  );

  useFocusEffect(
    useCallback(() => {
      if (assessment?.getAssessment?.data) {
        const timeLimitMinutes = assessment.getAssessment.data.timeLimit;
        const timeLimitSeconds = timeLimitMinutes > 0 ? timeLimitMinutes * 60 : 0;
        setAssessmentTimeLimit(timeLimitMinutes);
        setAssessmentNumberOfQuestions(assessment.getAssessment.data.numberOfQuestions);
        setDisplayTime(timeLimitMinutes > 0 ? formatCountdownFromSeconds(timeLimitSeconds) : null);
      }
    }, [assessment?.getAssessment?.data])
  );

  useEffect(() => {
    return () => {
      cleanupCountdown();
      stopAssessmentAudio();
    };
  }, [cleanupCountdown, stopAssessmentAudio]);

  const handleExitPage = () => {
    stopAssessmentAudio();
    if (stayOnCompletion) {
      return;
    }

    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      if (moduleId) {
        navigation.navigate("Custom Module Student", {
          module_id: moduleId,
          course_id: courseId,
          integration_id: typeof integrationId === "string" ? integrationId : courseId,
        });
      } else {
        navigation.navigate("Custom Course Student", {
          course_id: courseId,
          integration_id: integrationId,
        });
      }
    }
  };

  const handleCancelAssessmentBegin = () => {
    setBeginModalVisible(false);
    handleExitPage();
  };

  const handleConfirmCompleteModal = () => {
    if (stayOnCompletion) {
      return;
    }

    setCompleteModalVisible(false);
    refetchCourseData();
    const timeLimitSeconds = hasTimeLimit ? assessmentTimeLimit * 60 : 0;
    setDisplayTime(hasTimeLimit ? formatCountdownFromSeconds(timeLimitSeconds) : null);
    handleExitPage();
  };

  const handleTerminateAssessment = async () => {
    if (!assessmentId) {
      return;
    }
    setExitModalVisible(false);

    cleanupCountdown();
    stopAssessmentAudio();

    if (oralAssessmentV2Enabled) {
      if (oralSessionSubmissionId) {
        await endOralAssessmentSessionMutation({
          variables: {
            submissionId: oralSessionSubmissionId,
          },
        }).catch(() => {
          captureAssessmentError("Error ending oral assessment session on terminate");
        });
      } else {
        captureAssessmentError("Oral session submission id missing on terminate");
      }

      setOralSessionSubmissionId(null);
      setLoadingAssessmentSubmission(false);
      await refetchCourseData();
      handleExitPage();
      return;
    }

    await endSseAssessment(() => {
      captureAssessmentError("Error ending assessment on server");
    });

    setLoadingAssessmentSubmission(false);

    await refetchCourseData();
    handleExitPage();
  };

  const handleAnotherAssessmentInProgressModalClick = () => {
    setAnotherAssessmentInProgressModalVisible(false);
    handleExitPage();
  };

  const handleExitButtonPress = () => {
    if (assessmentIsRunning) {
      setExitModalVisible(true);
    } else {
      handleExitPage();
    }
  };

  const handleAssessmentErrorModalClick = () => {
    setAssessmentErrorModalVisible(false);
    setAssessmentErrorMessage("An error occurred while running the assessment. Please try again.");
    handleExitPage();
  };

  const startRecording = async (): Promise<boolean> => {
    try {
      const permission = await Audio.requestPermissionsAsync();
      if (!permission.granted) {
        captureAssessmentError("Microphone permission denied");
        setRecordingAnnouncement("Microphone permission is required to answer");
        return false;
      }

      await Audio.setAudioModeAsync({
        allowsRecordingIOS: true,
        playsInSilentModeIOS: true,
      });

      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      const { recording: expoAvRecording } = await Audio.Recording.createAsync({
        ...Audio.RecordingOptionsPresets.HIGH_QUALITY,
        web: {
          mimeType: isSafari ? "video/mp4" : "audio/webm",
          bitsPerSecond: 128000,
        },
      });
      setRecording(expoAvRecording);
      setIsRecording(true);
      setRecordingAnnouncement("Recording started");
      return true;
    } catch (error) {
      captureAssessmentError("Error starting recording", error);
      setRecordingAnnouncement("Unable to start recording. Please check microphone access.");
      return false;
    }
  };

  async function stopRecording() {
    if (!recording) {
      return;
    }

    setIsProcessingAnswer(true);
    setCanAnswer(false);

    await recording.stopAndUnloadAsync();
    const uri = recording.getURI();
    setRecording(null);
    setIsRecording(false);
    setRecordingAnnouncement("Recording stopped");

    const recordingEndedAtMs = Date.now();

    try {
      let fileString;

      if (Platform.OS === "web") {
        const response = await fetch(uri);
        const blob = await response.blob();

        if (oralAssessmentV2Enabled) {
          const file = new File([blob], "recording.webm", { type: blob.type || "audio/webm" });
          submitV2AnswerFromAudioSource({
            audioSource: file,
            recordingEndedAtMs,
            questionAudioTranscriptionLabel: "Web audio transcription failed",
          });
          return;
        }

        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onloadend = async () => {
          fileString = (reader.result as string).split(",")[1]; // Get the Base64 content
          await sendAnswerToServer(fileString);
        };
      } else {
        if (oralAssessmentV2Enabled) {
          const audioSource = {
            uri,
            name: "recording.m4a",
            type: "audio/m4a",
          };

          submitV2AnswerFromAudioSource({
            audioSource,
            recordingEndedAtMs,
            questionAudioTranscriptionLabel: "Native audio transcription failed",
          });

          return;
        }

        const FileSystem = require("expo-file-system");
        fileString = await FileSystem.readAsStringAsync(uri, {
          encoding: FileSystem.EncodingType.Base64,
        });
        await sendAnswerToServer(fileString);
      }
    } catch (error) {
      captureAssessmentError("Error stopping recording", error);
      setIsProcessingAnswer(false);
      setCanAnswer(true);
    }
  }

  const sendAnswerToServer = useCallback(
    async (audioBase64: string) => {
      if (continueAssessmentInFlightRef.current) {
        return;
      }
      continueAssessmentInFlightRef.current = true;
      try {
        setCurrentQuestionText(null);
        setCurrentQuestionAudio(null);
        if (resetSequenceRef.current) {
          resetSequenceRef.current();
        }
        audioQueueRef.current = [];

        const result = await continueSseAssessment(
          assessmentId,
          audioBase64,
          () => {
            captureAssessmentError("Error continuing assessment");
            setIsProcessingAnswer(false);
            setCanAnswer(true);
          },
          (chunk) => {
            // Handle chunk event
            setCurrentQuestionText((prev) => (prev ? `${prev}${chunk.text}` : chunk.text));
            if (audioQueueCallbackRef.current) {
              audioQueueCallbackRef.current(chunk);
            } else {
              audioQueueRef.current.push(chunk);
              setCurrentQuestionAudio(chunk.audio);
            }
            lastChunkAtRef.current = Date.now();
          },
          (data) => {
            // Handle state_update event
            if (data.questionIndex !== undefined) {
              setQuestionIndex(data.questionIndex + 1);
            }
            if (data.awaitingAnswer) {
              setCanAnswer(true);
              setIsProcessingAnswer(false);
            }
          },
          () => {
            // Handle done event
            if (audioQueueCallbackRef.current) {
              while (audioQueueRef.current.length > 0) {
                const queued = audioQueueRef.current.shift();
                if (queued) {
                  audioQueueCallbackRef.current(queued);
                }
              }
            }
            if (processRemainingChunksRef.current) {
              processRemainingChunksRef.current();
            }
            setTimeout(() => {
              setCanAnswer(true);
              setIsProcessingAnswer(false);
            }, 1000);
          },
          () => {
            setCanAnswer(true);
            setIsProcessingAnswer(false);
          }
        );

        if (result.completed) {
          setIsAssessmentCompleting(true);
          setIsProcessingAnswer(false);
          setCanAnswer(false);
          handleAssessmentComplete();
        }
      } catch (error) {
        captureAssessmentError("Error sending answer to server", error);
        setIsProcessingAnswer(false);
        setCanAnswer(true);
      } finally {
        continueAssessmentInFlightRef.current = false;
      }
    },
    [assessmentId, captureAssessmentError, handleAssessmentComplete, resetSequenceRef, continueAssessmentInFlightRef]
  );

  const handleAnswer = async () => {
    if (isRecording) {
      await stopRecording();
    } else if (canAnswer && !isProcessingAnswer) {
      const didStartRecording = await startRecording();
      if (didStartRecording && stopAudioRef.current) {
        stopAudioRef.current();
      }
    }
  };

  if (assessmentLoading || assessmentSubmissionLoading) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <Loader />
        </View>
      </BlankLayout>
    );
  }
  if (assessmentError || assessmentSubmissionError || !assessment?.getAssessment?.data) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="Something went wrong! Try reloading the page." />
        </View>
      </BlankLayout>
    );
  }
  const hasLinkedAiTutors = (assessment?.getAssessment?.data?.aiTutors?.length ?? 0) > 0;
  const hasLtiFreeTextInstructions =
    stayOnCompletion && Boolean(assessment?.getAssessment?.data?.freeTextInstructions?.trim());
  const assessmentHasNoQuestions = !hasLinkedAiTutors && !hasLtiFreeTextInstructions;
  if (assessmentHasNoQuestions) {
    return (
      <BlankLayout>
        <View style={styles.loaderContainer}>
          <ErrorLoader message="This assessment is not enabled." />
        </View>
      </BlankLayout>
    );
  }

  const showAssessmentAsRunningOrLoading =
    !assessmentErrorModalVisible &&
    !anotherAssessmentInProgressModalVisible &&
    !beginModalVisible &&
    !completeModalVisible;

  const moduleName = assessment?.getAssessment?.data?.moduleHasAssessment?.title;

  return (
    <BlankLayout>
      <View style={styles.mainContainer}>
        {/* @ts-expect-error -- role="heading" and aria-level are web-only ARIA props not in RN types */}
        <View
          style={styles.srOnly}
          role="heading"
          aria-level={1}
          accessibilityRole="header"
          accessibilityLabel={moduleName ? `Assessment: ${moduleName}` : "Assessment"}>
          <CustomText
            text={moduleName ?? "Assessment"}
            useTranslationText={false}
            accessibilityElementsHidden={false}
          />
        </View>
        <View
          style={styles.srOnly}
          role="status"
          aria-live="assertive"
          aria-atomic={true}
          accessibilityLiveRegion="assertive"
          accessible={true}>
          <CustomText
            text={recordingAnnouncement}
            useTranslationText={false}
            accessibilityLabel={recordingAnnouncement}
          />
        </View>
        <AssessmentMenu
          handleExitPress={handleExitButtonPress}
          remainingTime={hasTimeLimit ? displayTime : null}
          moduleName={moduleName}
          hasMicrophoneControls={true}
          timeIsLoading={hasTimeLimit && !displayTime}
          isSseAssessment={true}
          selectedDeviceId={selectedMicrophoneId}
          onDeviceSelect={handleMicrophoneSelect}
          timerAnnouncement={timerAnnouncement}
          timerAnnouncementAssertive={timerAnnouncementAssertive}
        />
        <View style={styles.contentContainer}>
          {showAssessmentAsRunningOrLoading && (
            <View style={styles.orbContainer}>
              <AssessmentSpeakingOrb
                dynamicHeight={dynamicHeight}
                orbLoading={orbLoading}
                setOrbLoading={setOrbLoading}
                useLiveKit={false}
                isPlaying={isIntroPlaying}
                audioLevel={audioLevel}
              />
            </View>
          )}
          {loadingAssessmentSubmission && (
            <View style={{ height: 10, justifyContent: "center", alignItems: "center" }}>
              <ActivityIndicator size="large" color="#6B7280" accessibilityLabel="Preparing assessment" />
              <CustomText text="Preparing assessment..." style={{ color: "#666" }} useTranslationText={false} />
            </View>
          )}
          {isProcessingAnswer && (
            <View style={{ justifyContent: "center", alignItems: "center", marginTop: 8 }}>
              <ActivityIndicator size="small" color="#6B7280" accessibilityLabel="Processing answer" />
              <CustomText text="Processing answer..." style={{ color: "#666" }} useTranslationText={false} />
            </View>
          )}
          <View style={styles.overlayContent}>
            <QuestionCounter questionIndex={questionIndex} totalQuestions={assessmentNumberOfQuestions} />
            {(assessmentIsRunning || introText || introAudio || currentQuestionText || currentQuestionAudio) && (
              <TurnBasedAssessmentAudioRenderer
                introText={currentQuestionText || undefined}
                introAudio={currentQuestionAudio || undefined}
                onIntroStart={handleRendererIntroStart}
                onIntroComplete={handleRendererIntroComplete}
                useStreaming={!oralAssessmentV2Enabled}
                preferUrlPlayback={oralAssessmentV2Enabled}
                onAudioLevel={(lvl) => setAudioLevel(lvl)}
                onAudioQueueReady={
                  oralAssessmentV2Enabled
                    ? undefined
                    : (callback) => {
                        audioQueueCallbackRef.current = callback;
                        while (audioQueueRef.current.length > 0) {
                          const queued = audioQueueRef.current.shift();
                          if (queued) {
                            callback(queued);
                          }
                        }
                      }
                }
                onStreamDone={
                  oralAssessmentV2Enabled
                    ? undefined
                    : (processRemaining) => {
                        processRemainingChunksRef.current = processRemaining;
                      }
                }
                onResetSequence={
                  oralAssessmentV2Enabled
                    ? undefined
                    : (resetFn) => {
                        resetSequenceRef.current = resetFn;
                      }
                }
                onStopAudio={(stopFn) => {
                  stopAudioRef.current = stopFn;
                }}
              />
            )}
            {questionAudioMissing && (
              <View style={styles.audioFallbackHintContainer}>
                <CustomText
                  text="Audio is unavailable for this question. Read the prompt text and answer when ready."
                  useTranslationText={false}
                  style={styles.audioFallbackHintText}
                />
              </View>
            )}
          </View>
          {!beginModalVisible && (
            <CustomButton
              text={
                isAssessmentCompleting
                  ? "Ending..."
                  : isProcessingAnswer
                    ? "Processing..."
                    : isRecording
                      ? "Stop recording"
                      : canAnswer
                        ? "Press to answer"
                        : "Waiting for question..."
              }
              onPress={handleAnswer}
              disabled={!canAnswer || isProcessingAnswer || isAssessmentCompleting}
              leftIcon={
                isAssessmentCompleting || isProcessingAnswer ? (
                  <View style={{ transform: [{ scale: 0.7 }] }}>
                    <ActivityIndicator size={20} color="#666" />
                  </View>
                ) : isRecording ? (
                  <Microphone size={20} weight="fill" color="#666" />
                ) : canAnswer ? (
                  <Microphone size={20} weight="regular" color="#666" />
                ) : (
                  <View style={{ transform: [{ scale: 0.7 }] }}>
                    <ActivityIndicator size={20} color="#666" />
                  </View>
                )
              }
              textStyle={{ color: "#666" }}
            />
          )}
          <View style={{ height: BOTTOM_SPACING_HEIGHT }} />
        </View>
      </View>

      <AssessmentBeginModal
        visible={beginModalVisible}
        onCancel={handleCancelAssessmentBegin}
        onConfirm={handleBeginAssessment}
        defaultTime={assessmentTimeLimit}
        defaultQuestionsCount={assessmentNumberOfQuestions}
        isLoading={loadingAssessmentSubmission}
        selectedDeviceId={selectedMicrophoneId}
        onDeviceSelect={handleMicrophoneSelect}
        skipLivekitCheck={true}
      />
      <AssessmentBlockedModal
        visible={assessmentBlockedModalVisible}
        onConfirm={() => {
          setAssessmentBlockedModalVisible(false);
          handleExitPage();
        }}
      />
      <AssessmentCompleteModal
        visible={completeModalVisible}
        onConfirm={handleConfirmCompleteModal}
        showExitButton={!stayOnCompletion}
      />
      <ExitBeforeCompletedModal
        visible={exitModalVisible}
        onConfirm={handleTerminateAssessment}
        onCancel={() => setExitModalVisible(false)}
        showExitButton={!stayOnCompletion}
      />
      <CursorLeavesWindowModal
        visible={cursorLeavesWindowModalVisible}
        onConfirm={() => setCursorLeavesWindowModalVisible(false)}
      />
      <AssessmentInProgressModal
        visible={anotherAssessmentInProgressModalVisible}
        onConfirm={handleAnotherAssessmentInProgressModalClick}
      />
      <AssessmentErrorModal
        visible={assessmentErrorModalVisible}
        onConfirm={handleAssessmentErrorModalClick}
        message={assessmentErrorMessage}
      />
    </BlankLayout>
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    flex: 1,
    justifyContent: "flex-end",
    alignItems: "center",
    paddingHorizontal: CONTENT_CONTAINER_PADDING,
    paddingBottom: CONTENT_CONTAINER_PADDING,
    paddingTop: 0,
    backgroundColor: "transparent",
    position: "relative",
  },
  orbContainer: {
    position: "absolute",
    bottom: CONTENT_CONTAINER_PADDING,
    left: CONTENT_CONTAINER_PADDING,
    right: CONTENT_CONTAINER_PADDING,
    top: 0,
    justifyContent: "flex-start",
    alignItems: "center",
    zIndex: 1,
    paddingTop: CONTENT_CONTAINER_PADDING,
    pointerEvents: "none",
  },
  overlayContent: {
    position: "relative",
    zIndex: 2,
    width: "100%",
    alignItems: "center",
    backgroundColor: "transparent",
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  mainContainer: {
    flex: 1,
    backgroundColor: "#E1E5FA",
  },
  srOnly: {
    position: "absolute",
    left: -10000,
    top: "auto",
    width: 1,
    height: 1,
    overflow: "hidden",
  },
  audioFallbackHintContainer: {
    marginTop: 8,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: "#F2F4F7",
  },
  audioFallbackHintText: {
    color: "#344054",
    textAlign: "center",
  },
});

export default TurnBasedAssessment;
